import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert, AlertTitle, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, Box, Typography,
} from '@mui/material';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import FileUpload from '../FileUpload';
import simpleVerificationService from '../../services/simpleVerificationService';

// Document types a verifier can submit as their government ID. The values map
// to user_documents.document_type on the backend (national_id -> id_card,
// drivers_license -> license, passport -> passport).
const ID_DOC_OPTIONS = [
  { value: 'national_id', label: 'National ID', name: 'National ID' },
  { value: 'passport', label: 'Passport', name: 'Passport' },
  { value: 'drivers_license', label: "Driver's license", name: "Driver's license" },
];

/**
 * Shown to verifiers who don't yet have an approved government ID. Lets them
 * upload one inline (the only always-reachable upload path for verifiers).
 * Renders nothing for non-verifiers or once an ID is approved.
 */
export default function VerifierIdentityBanner() {
  const [eligibility, setEligibility] = useState(null);
  const [open, setOpen] = useState(false);
  const [docType, setDocType] = useState('national_id');
  const [justUploaded, setJustUploaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await simpleVerificationService.getEligibility();
      setEligibility(res?.data || null);
    } catch (e) {
      setEligibility(null); // fail quiet — don't block the page on a banner check
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!eligibility || !eligibility.applicable || eligibility.identityDocumentApproved) {
    return null;
  }

  const status = eligibility.identityDocumentStatus; // 'missing' | 'pending' | 'rejected'
  const selected = ID_DOC_OPTIONS.find((o) => o.value === docType) || ID_DOC_OPTIONS[0];

  const onUploaded = async () => {
    setJustUploaded(true);
    setOpen(false);
    await load();
  };

  // Uploaded and awaiting admin review — informational, no action needed.
  if (status === 'pending' || justUploaded) {
    return (
      <Alert severity="info" icon={<BadgeOutlinedIcon />} sx={{ mb: 3 }}>
        <AlertTitle>ID under review</AlertTitle>
        Thanks — your government ID has been submitted. You'll be able to accept verification work
        as soon as our team approves it.
      </Alert>
    );
  }

  // Missing or rejected — prompt upload.
  return (
    <>
      <Alert
        severity="warning"
        icon={<BadgeOutlinedIcon />}
        sx={{ mb: 3 }}
        action={
          <Button color="inherit" size="small" variant="outlined" onClick={() => setOpen(true)}>
            Upload ID
          </Button>
        }
      >
        <AlertTitle>{status === 'rejected' ? 'Re-upload your government ID' : 'Upload your government ID'}</AlertTitle>
        {status === 'rejected'
          ? 'The ID you submitted couldn’t be verified. Please upload a clear copy of your National ID, passport, or driver’s license to keep accepting verification work.'
          : 'To accept verification work you need a government ID on file (National ID, passport, or driver’s license). Upload one now — our team will review and unlock your account.'}
      </Alert>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Upload your government ID</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose the document type, then upload a clear photo or scan. Accepted: National ID,
            passport, or driver's license.
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="id-doc-type-label">Document type</InputLabel>
            <Select
              labelId="id-doc-type-label"
              label="Document type"
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
            >
              {ID_DOC_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box>
            <FileUpload
              uploadType="documents"
              accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,.webp"
              maxSize={10 * 1024 * 1024}
              onUploadComplete={onUploaded}
              buttonText={`Upload ${selected.label}`}
              additionalData={{
                documentType: selected.value,
                documentCategory: 'identity',
                documentName: selected.name,
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
